<template>
  <div
    class="ppa-instructions-editor"
    :class="{ 'is-wizard': mode === 'wizard' }"
  >
    <WizardStepRail
      v-if="mode === 'wizard'"
      :steps="wizardSteps"
      :currentIndex="currentStep"
      :stepLabel="$t('common.step')"
    />

    <!-- Hoisted above the paged body so the AI chat stays mounted across every
         wizard step (a v-if inside a step would unmount it and reset the
         conversation). The card keeps a single conversation for the whole
         editing session and can prefill fields regardless of the active step. -->
    <div v-if="portfolio.company_id" class="ppa-instructions-editor__assistant">
      <WizardSection
        :title="$t('production_strategy.prefill_from_document')"
        :optional="true"
      >
        <PPAOptionalUploadCard
          v-model="actionInstructions"
          :companyId="portfolio.company_id"
          @analysis-complete="handleAnalysisComplete"
        />
      </WizardSection>
    </div>

    <div class="ppa-instructions-editor__body">
      <!-- Step 1 / Action (wizard) OR first stack block (all) -->
      <template v-if="mode === 'all' || currentStep === 0">
        <WizardSection
          :title="$t('production_strategy.portfolio_name_title')"
          :description="$t('production_strategy.portfolio_name_desc')"
        >
          <label
            for="ppa-portfolio-name-input"
            class="ppa-instructions-editor__field-label"
          >
            {{ $t("common.name") }}
          </label>
          <input
            id="ppa-portfolio-name-input"
            data-testid="portfolio-name-input"
            type="text"
            :class="['form-control', { 'is-invalid': shouldShowNameError }]"
            v-model="nameInputValue"
            :placeholder="fallbackPortfolioName"
            @blur="v$.name.$touch()"
          />
          <div v-if="shouldShowNameError" class="invalid-feedback d-block">
            {{ nameErrors[0] }}
          </div>
        </WizardSection>

        <WizardSection
          :title="$t('production_strategy.contract_dates_title')"
          :description="$t('production_strategy.contract_dates_desc')"
        >
          <ContractDatesSection
            ref="contractDatesSectionRef"
            v-model="actionInstructions"
            :granularity="selectedGranularity"
            @update:modelValue="updateActionInstructions"
          />
        </WizardSection>

        <WizardSection
          :title="$t('production_strategy.action_title')"
          :description="$t('production_strategy.action_desc')"
        >
          <p v-if="usesCnmcIssuance && destinationsLoading" role="status">
            {{ $t("common.loading") }}
          </p>
          <button
            v-else-if="usesCnmcIssuance && destinationsError"
            type="button"
            class="btn btn-outline-primary"
            @click="loadDestinations()"
          >
            {{ $t("common.retry") }}
          </button>
          <CounterpartyDetailsSection
            v-if="!usesCnmcIssuance || destinationsReady"
            ref="counterpartyDetailsSectionRef"
            :usesCnmcIssuance
            :cnmcDestinations
            v-model="actionInstructions"
            @update:modelValue="updateActionInstructions"
          />
        </WizardSection>
      </template>

      <!-- Step 2 / Allocation -->
      <template v-if="mode === 'all' || currentStep === 1">
        <WizardSection
          :title="$t('production_strategy.allocation_title')"
          :description="$t('production_strategy.allocation_desc')"
        >
          <AllocationSection
            :modelValue="portfolio.portfolio_instructions"
            :percentageErrors="percentageErrors"
            :limitsErrors="limitsErrors"
            :contractStart="actionInstructions?.start_date ?? null"
            :contractEnd="actionInstructions?.end_date ?? null"
            @update:modelValue="updatePortfolioInstructions"
            @field-touched="handleFieldTouched"
          />
        </WizardSection>

        <WizardSection
          :title="$t('production_strategy.devices_title')"
          :description="$t('production_strategy.devices_desc')"
        >
          <DeviceSelectionSection
            ref="deviceSelectionSectionRef"
            :modelValue="portfolio.portfolio_instructions"
            :registryDevices="registryDevices"
            :existingPortfolios="existingPortfolios"
            :currentPortfolioId="portfolio.id"
            @update:modelValue="updatePortfolioInstructions"
          />
        </WizardSection>
      </template>

      <!-- Step 3 / Execution & review -->
      <template v-if="mode === 'all' || currentStep === 2">
        <WizardSection
          :title="$t('production_strategy.execution_title')"
          :description="
            usesCnmcIssuance
              ? $t('cnmc.ppa_execution_desc')
              : $t('production_strategy.execution_desc')
          "
        >
          <InfoCallout v-if="usesCnmcIssuance" icon="file-lines">
            {{ $t("cnmc.ppa_execution_help") }}
          </InfoCallout>
          <DeliveryTimingSection
            v-else
            ref="deliveryTimingSectionRef"
            v-model="actionInstructions"
            @update:modelValue="updateActionInstructions"
          />
        </WizardSection>

        <WizardSection
          :title="$t('production_strategy.review_title')"
          :description="$t('production_strategy.review_desc')"
          icon="list-check"
        >
          <div class="ppa-instructions-editor__review">
            <PPASummary
              v-if="!usesCnmcIssuance"
              :company-id="portfolio.company_id"
              :action-instructions="actionInstructions"
              :portfolio-instructions="portfolio.portfolio_instructions"
              :registry-devices="registryDevices"
              :individual-allocation="individualAllocation"
            />
            <PPAReviewSummary
              :usesCnmcIssuance
              :actionInstructions="actionInstructions"
              :portfolioInstructions="portfolio.portfolio_instructions"
              :registryDevices="registryDevices"
            />
          </div>
        </WizardSection>

        <WizardSection
          :title="$t('production_strategy.notes_title')"
          :description="$t('production_strategy.notes_desc')"
          :optional="true"
        >
          <NotesSection
            :modelValue="actionInstructions?.notes || ''"
            @update:modelValue="updateActionNotesField"
          />
        </WizardSection>
      </template>
    </div>

    <WizardFooter v-if="mode === 'wizard'">
      <template #start>
        <button
          v-if="currentStep > 0"
          type="button"
          class="btn btn-ghost"
          @click="goBack"
        >
          <FontAwesomeIcon icon="arrow-left" class="me-2" />
          {{ $t("common.back") }}
        </button>
        <button
          v-else
          type="button"
          class="btn btn-link ppa-instructions-editor__show-all"
          @click="showAll"
        >
          <FontAwesomeIcon icon="list" class="me-2" />
          {{ $t("production_strategy.show_all_as_one_form") }}
        </button>
      </template>
      <template #end>
        <span class="ppa-instructions-editor__step-indicator">
          {{
            $t("production_strategy.step_x_of_y", {
              current: currentStep + 1,
              total: wizardSteps.length,
            })
          }}
        </span>
        <button type="button" class="btn btn-ghost" @click="handleCancel">
          {{ $t("common.cancel") }}
        </button>
        <button
          v-if="!isLastStep"
          type="button"
          class="btn btn-primary"
          @click="goNext"
        >
          {{ $t("common.continue") }}
          <FontAwesomeIcon icon="arrow-right" class="ms-2" />
        </button>
        <button
          v-else
          type="button"
          class="btn btn-primary"
          @click="handleSave"
        >
          <FontAwesomeIcon icon="floppy-disk" class="me-2" />
          {{ $t("production_strategy.save_ppa") }}
        </button>
      </template>
    </WizardFooter>

    <WizardFooter v-else-if="showAllModeFooter">
      <template #end>
        <button type="button" class="btn btn-ghost" @click="handleCancel">
          {{ $t("common.cancel") }}
        </button>
        <button type="button" class="btn btn-primary" @click="handleSave">
          <FontAwesomeIcon icon="floppy-disk" class="me-2" />
          {{ $t("common.save") }}
        </button>
      </template>
    </WizardFooter>
  </div>
</template>

<script setup lang="ts">
import { useAsyncState } from "@vueuse/core";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { cnmcApi } from "@/dashboard/production_strategy/issuance-reporting/es/cnmcApi";
import InfoCallout from "@/components/common/InfoCallout.vue";
import WizardStepRail from "@/components/common/WizardStepRail.vue";
import { useValidators } from "@/composables/useValidators";
import AllocationSection from "@/dashboard/production_strategy/overview/PPA/components/AllocationSection.vue";
import ContractDatesSection from "@/dashboard/production_strategy/overview/PPA/components/ContractDatesSection.vue";
import CounterpartyDetailsSection from "@/dashboard/production_strategy/overview/PPA/components/CounterpartyDetailsSection.vue";
import DeliveryTimingSection from "@/dashboard/production_strategy/overview/PPA/components/DeliveryTimingSection.vue";
import DeviceSelectionSection from "@/dashboard/production_strategy/overview/PPA/components/DeviceSelectionSection.vue";
import NotesSection from "@/dashboard/production_strategy/overview/PPA/components/NotesSection.vue";
import PPAOptionalUploadCard from "@/dashboard/production_strategy/overview/PPA/components/PPAOptionalUploadCard.vue";
import PPAReviewSummary from "@/dashboard/production_strategy/overview/PPA/components/PPAReviewSummary.vue";
import PPASummary from "@/dashboard/production_strategy/overview/PPA/components/PPASummary.vue";
import WizardFooter from "@/components/common/WizardFooter.vue";
import WizardSection from "@/components/common/WizardSection.vue";
import {
  createDefaultActionInstructions,
  initializeRecurringDateSpecs,
  snapToMonthEnd,
  snapToMonthStart,
} from "@/dashboard/production_strategy/overview/PPA/ppaActionUtils";
import {
  applyReferencePattern,
  determineAllocationMode,
  determineLimitGranularity,
  hasHomogeneousDurations,
  parseIsoDate,
} from "@/dashboard/production_strategy/overview/PPA/ppaPortfolioUtils";
import type { PPAIndividualAllocation } from "@/dashboard/production_strategy/overview/useProductionAllocationData";
import type {
  ActionInstructions,
  LimitGranularity,
  PeriodLimit,
  PortfolioInstructions,
  PortfolioInstructionsPatch,
  PortfolioWithInstructions,
  RegistryDevice,
} from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useVuelidate } from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import { computed, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

export type PPAEditorMode = "wizard" | "all";

const props = withDefaults(
  defineProps<{
    registryDevices: RegistryDevice[];
    usesCnmcIssuance: boolean;
    existingPortfolios?: PortfolioWithInstructions[];
    individualAllocation?: PPAIndividualAllocation | null;
    mode?: PPAEditorMode;
    showAllModeFooter?: boolean;
  }>(),
  { mode: "all", showAllModeFooter: false }
);

const emit = defineEmits<{
  "error:ai-merge": [error: Error | string];
  save: [];
  cancel: [];
  "step-change": [stepIndex: number];
  "show-all": [];
}>();

const portfolio = defineModel<PortfolioWithInstructions>({ required: true });

const { t } = useI18n();
const customValidators = useValidators(t);

const deliveryTimingSectionRef = useTemplateRef<
  InstanceType<typeof DeliveryTimingSection>
>("deliveryTimingSectionRef");
const counterpartyDetailsSectionRef = useTemplateRef<
  InstanceType<typeof CounterpartyDetailsSection>
>("counterpartyDetailsSectionRef");
const deviceSelectionSectionRef = useTemplateRef<
  InstanceType<typeof DeviceSelectionSection>
>("deviceSelectionSectionRef");
const contractDatesSectionRef = useTemplateRef<
  InstanceType<typeof ContractDatesSection>
>("contractDatesSectionRef");

// Reactive action instructions
const actionInstructions = ref<ActionInstructions>(
  portfolio.value?.action_instructions ?? createDefaultActionInstructions()
);

const { globalErrorHandler } = useGlobalHandlers();
const {
  state: cnmcDestinations,
  isReady: destinationsReady,
  isLoading: destinationsLoading,
  error: destinationsError,
  execute: loadDestinations,
} = useAsyncState(
  async () =>
    (await cnmcApi.context(portfolio.value.company_id)).data.destinations,
  [],
  { immediate: false, onError: globalErrorHandler }
);
watch(
  () => props.usesCnmcIssuance,
  (enabled) => {
    if (enabled && !destinationsReady.value) void loadDestinations();
  },
  { immediate: true }
);

// Drives the ContractDatesSection partial-period hint. Deliberately `null`
// until concrete limits exist (either from an AI analysis or from the user
// configuring allocation) — otherwise a premature "yearly" default would
// surface a confusing "1 period is partial" banner the moment the user
// types a mid-year start date before they've picked a cadence.
const selectedGranularity = computed<LimitGranularity | null>(() => {
  const limits = portfolio.value.portfolio_instructions.limits;
  if (!limits || limits.length === 0) return null;
  return determineLimitGranularity(limits);
});

const initialPortfolioName = (portfolio.value.name ?? "").trim();
const portfolioName = ref(initialPortfolioName);
const hasTouchedName = ref(initialPortfolioName.length > 0);

// Wizard state
const currentStep = ref(0);
const wizardSteps = computed(() => [
  { title: t("production_strategy.wizard_step_action") },
  { title: t("production_strategy.wizard_step_allocation") },
  { title: t("production_strategy.wizard_step_execution") },
]);
const isLastStep = computed(
  () => currentStep.value === wizardSteps.value.length - 1
);

watch(currentStep, (step) => emit("step-change", step));

// Determine allocation mode for validation
const allocationMode = computed(() =>
  determineAllocationMode(
    portfolio.value.portfolio_instructions.allocate_percentage,
    portfolio.value.portfolio_instructions.limits ?? []
  )
);

const limitGranularity = computed(() =>
  determineLimitGranularity(portfolio.value.portfolio_instructions.limits)
);

// Validation rules for allocation section (remaining parent-level validation)
const rules = computed(() => ({
  name: {
    required: customValidators.required,
  },
  allocate_percentage: {
    required: customValidators.required,
    between:
      allocationMode.value === "percentage"
        ? customValidators.between(0.01, 100)
        : customValidators.between(0, 100),
  },
  limits: {
    fixedAmountsPositive: helpers.withMessage(
      t("portfolio_instructions.fixed_amount_must_be_gt_zero"),
      (limits: PeriodLimit[]) => {
        if (allocationMode.value !== "fixed-amounts") return true;
        if (!Array.isArray(limits) || limits.length === 0) return false;
        return limits.every((l) => Number(l?.limit_mwh) > 0);
      }
    ),
    homogeneousDurations: helpers.withMessage(
      t("portfolio_instructions.invalid_limit_durations_for_granularity"),
      (limits: PeriodLimit[]) => {
        if (allocationMode.value !== "fixed-amounts") return true;
        return hasHomogeneousDurations(limits, limitGranularity.value);
      }
    ),
    startEndRequired: helpers.withMessage(
      t("portfolio_instructions.contract_range_required_for_granularity"),
      (limits: PeriodLimit[]) => {
        if (allocationMode.value !== "fixed-amounts") return true;
        if (limitGranularity.value === "yearly") return true;
        if (!Array.isArray(limits) || limits.length === 0) return true;
        const instr = actionInstructions.value;
        if (!instr.start_date || !instr.end_date) return false;
        return (
          parseIsoDate(instr.start_date).getTime() <
          parseIsoDate(instr.end_date).getTime()
        );
      }
    ),
  },
}));

const v$ = useVuelidate(
  rules,
  computed(() => ({
    name: portfolioName.value.trim(),
    allocate_percentage:
      portfolio.value.portfolio_instructions.allocate_percentage,
    limits: portfolio.value.portfolio_instructions.limits,
  })),
  {
    $autoDirty: true,
  }
);

const nameErrors = computed(
  () => v$.value.name.$errors?.map((e) => e.$message as string) || []
);

const shouldShowNameError = computed(
  () => v$.value.name.$dirty && nameErrors.value.length > 0
);

// Computed error arrays for allocation section
const percentageErrors = computed(
  () =>
    v$.value.allocate_percentage.$errors?.map((e) => e.$message as string) || []
);

const limitsErrors = computed(
  () => v$.value.limits.$errors?.map((e) => e.$message as string) || []
);

// Update methods
const defaultPortfolioName = computed(() => {
  const instructions = actionInstructions.value;
  if (!instructions) return null;

  // `|| null` (not `?? null`) so an empty/whitespace-only counterparty name
  // falls through to the placeholder fallback instead of being returned as
  // an empty string — otherwise `fallbackPortfolioName`'s `??` chain never
  // hands off to the i18n placeholder.
  if (instructions.transaction_type === "SEND" && instructions.send_template) {
    return instructions.send_template.recipient_company_name?.trim() || null;
  }

  if (
    instructions.transaction_type === "CANCEL" &&
    instructions.cancel_statement
  ) {
    return instructions.cancel_statement.beneficiary_name?.trim() || null;
  }

  return null;
});

const fallbackPortfolioName = computed(
  () => defaultPortfolioName.value ?? t("ppa_strategy.ppa_agreement")
);

const updatePortfolioName = (name: string) => {
  if ((portfolio.value.name ?? "").trim() === name) {
    return;
  }

  portfolio.value = { ...portfolio.value, name };
};

const nameInputValue = computed({
  get: () => portfolioName.value,
  set: (value: string) => {
    hasTouchedName.value = true;
    const trimmedValue = value.trim();
    portfolioName.value = trimmedValue;
    updatePortfolioName(trimmedValue);
  },
});

const updateActionInstructions = () => {
  portfolio.value = {
    ...portfolio.value,
    action_instructions: actionInstructions.value,
  };
};

const updatePortfolioInstructions = (instructions: PortfolioInstructions) => {
  portfolio.value = {
    ...portfolio.value,
    portfolio_instructions: instructions,
  };
};

const updateActionNotesField = (notes: string | null | undefined) => {
  actionInstructions.value.notes = notes || undefined;
  updateActionInstructions();
};

watch(
  () => ({
    id: portfolio.value.id,
    name: portfolio.value.name,
  }),
  (newValue, oldValue) => {
    const normalizedName = (newValue.name ?? "").trim();

    if (portfolioName.value !== normalizedName) {
      portfolioName.value = normalizedName;
    }

    if (!oldValue || newValue.id !== oldValue.id) {
      hasTouchedName.value = normalizedName.length > 0;
      return;
    }

    if (normalizedName.length === 0) {
      hasTouchedName.value = false;
    }
  },
  { immediate: true }
);

// Auto-fill the portfolio name from the counterparty (recipient for SEND,
// beneficiary for CANCEL) while the user hasn't typed anything of their own.
// Watches `defaultPortfolioName` (which is null until counterparty data
// exists) so the placeholder-only fallback ("PPA agreement") never gets
// written into the field as a literal value.
watch(
  defaultPortfolioName,
  (newDefault) => {
    if (!newDefault || hasTouchedName.value) return;
    const trimmedName = newDefault.trim();
    if (portfolioName.value !== trimmedName) {
      portfolioName.value = trimmedName;
      updatePortfolioName(trimmedName);
    }
  },
  { immediate: true }
);

// Fields the AI extractor is allowed to write into `action_instructions`.
// `custom_user_upload_ids` is owned by `DocumentsUploadSection` — it maintains
// the full upload list and emits `update:modelValue` before
// `analysis-complete`, so re-applying the AI's (per-request) list would
// truncate earlier uploads.
const AI_MERGED_ACTION_FIELDS = [
  "delivery_method",
  "delivery_timing",
  "recurring_date_specs",
  "start_date",
  "end_date",
  "transaction_type",
  "send_template",
  "cancel_statement",
  "notes",
] as const satisfies readonly (keyof ActionInstructions)[];

type AiMergedField = (typeof AI_MERGED_ACTION_FIELDS)[number];

/** Returned by a field merger that wants the field left at its current value. */
const KEEP_CURRENT = Symbol("keep-current");

/**
 * Per-field exceptions to the "AI wins" rule in `handleAnalysisComplete`; a field without an entry is
 * copied across verbatim. A merger receives the extracted value — `null` meaning the extractor did not
 * find the field — plus the current value, and returns either the value to store or `KEEP_CURRENT`.
 */
const AI_FIELD_MERGERS: {
  [K in AiMergedField]?: (
    incoming: NonNullable<ActionInstructions[K]> | null,
    current: ActionInstructions[K]
  ) => ActionInstructions[K] | typeof KEEP_CURRENT;
} = {
  // AI-supplied contract dates must land on the same month boundary the manual-entry path produces;
  // otherwise the backend rejects the save with "start_date must be first-of-month" for monthly /
  // quarterly PPAs. Snap-returns-null means unparseable — leave the existing value intact.
  start_date: (incoming) =>
    incoming === null ? null : (snapToMonthStart(incoming) ?? KEEP_CURRENT),
  // end_date is non-nullable, so an extraction that found nothing cannot clear it.
  end_date: (incoming) =>
    incoming === null
      ? KEEP_CURRENT
      : (snapToMonthEnd(incoming) ?? KEEP_CURRENT),
  // DEV-583: replacing cancel_statement wholesale would silently clear a manually-set privacy
  // preference. `exclude_none` drops fields the extractor didn't find, so an absent `grexel_options`
  // means "the document didn't say" — carry the existing choice over. A present one is an explicit
  // document statement and wins like every other extracted field.
  cancel_statement: (incoming, current) =>
    incoming === null
      ? null
      : {
          ...incoming,
          grexel_options: incoming.grexel_options ?? current?.grexel_options,
        },
};

// AI-extracted limits patch that arrived before start_date/end_date were
// set. The watcher below retries the merge once both dates are populated.
const pendingLimitsPatch = ref<PortfolioInstructionsPatch | null>(null);

// Apply an AI `portfolio_instructions_patch` to the current
// `portfolio_instructions`. A fully-expanded `limits[]` wins straight
// away; a `reference_year_values` pattern is expanded against the
// contract range and deferred when the range isn't known yet (the
// watcher below replays the patch when both dates arrive).
type MergeResult = {
  applied: PortfolioInstructions | null;
  deferred: boolean;
  // True when expansion failed with dates already set — patch is unusable,
  // callers should clear it and surface the error.
  failed: boolean;
};

const mergeLimitsPatchIntoModel = (
  patch: PortfolioInstructionsPatch
): MergeResult => {
  const withLimits = (limits: PeriodLimit[]): PortfolioInstructions => ({
    ...portfolio.value.portfolio_instructions,
    limits,
  });

  if (patch.limits && patch.limits.length > 0) {
    return {
      applied: withLimits(patch.limits),
      deferred: false,
      failed: false,
    };
  }
  if (!patch.reference_year_values) {
    return { applied: null, deferred: false, failed: false };
  }
  const start = actionInstructions.value.start_date;
  const end = actionInstructions.value.end_date;
  if (!start || !end) {
    return { applied: null, deferred: true, failed: false };
  }
  // Inverted / equal range is a transient editing state — keep the pending
  // patch and retry once the user fixes the dates, rather than surfacing a
  // merge error for an arithmetic intermediate.
  const startDate = parseIsoDate(start);
  const endDate = parseIsoDate(end);
  if (startDate >= endDate) {
    return { applied: null, deferred: true, failed: false };
  }

  const expanded = applyReferencePattern(patch, startDate, endDate);
  if (expanded.length === 0) {
    return { applied: null, deferred: false, failed: true };
  }
  return { applied: withLimits(expanded), deferred: false, failed: false };
};

// Handle AI analysis results from the upload card. Design decision: the
// AI analysis is the source of truth for the uploaded document — every
// field the extractor returned overwrites the current value. Re-uploads
// therefore replace edits the user made between uploads, which we
// accept in exchange for a dramatically simpler merge.
//
// `extractedData` is typed `unknown` to match the emit signature of
// `PPAOptionalUploadCard`; we narrow via a shallow runtime check before
// copying fields across.
const handleAnalysisComplete = (
  extractedData: unknown,
  portfolioInstructionsPatch?: PortfolioInstructionsPatch
) => {
  try {
    if (extractedData && typeof extractedData === "object") {
      const source = extractedData as Partial<ActionInstructions>;
      for (const key of AI_MERGED_ACTION_FIELDS) {
        const value = source[key];
        // Skip only missing fields. An explicit `null` means the extractor
        // couldn't find the field in the re-uploaded document, and "AI
        // wins" requires it to clear any stale value left over from an
        // earlier upload rather than silently preserving it — unless the
        // field's merger in AI_FIELD_MERGERS says otherwise.
        if (value === undefined) continue;
        // The mergers are internally typed per field; the index signature can't be correlated to a
        // union key, so erase it here rather than at each merger.
        const merge = AI_FIELD_MERGERS[key] as
          ((incoming: unknown, current: unknown) => unknown) | undefined;
        const merged = merge
          ? merge(value, actionInstructions.value[key])
          : value;
        if (merged === KEEP_CURRENT) continue;
        (actionInstructions.value as Record<string, unknown>)[key] = merged;
      }

      // Transaction-type exclusivity is a backend invariant (SEND must
      // have no cancel_statement, CANCEL must have no send_template).
      // Enforce it here so a SEND→CANCEL flip doesn't leave the stale
      // counterpart sitting around.
      if (actionInstructions.value.transaction_type === "SEND") {
        actionInstructions.value.cancel_statement = null;
      } else if (actionInstructions.value.transaction_type === "CANCEL") {
        actionInstructions.value.send_template = null;
      }

      // An AI-supplied timing is a timing change like picking it in the dropdown, so the date
      // specs must be re-initialized for it the same way. Without this a MONTHLY extraction
      // that carries no recurring_date_specs keeps the seeded spec's month, which the backend
      // rejects ("Monthly delivery should use month=1").
      if (source.delivery_timing) {
        actionInstructions.value = initializeRecurringDateSpecs(
          actionInstructions.value,
          actionInstructions.value.delivery_timing
        );
      }
    }

    let nextPortfolioInstructions: PortfolioInstructions | null = null;
    if (portfolioInstructionsPatch) {
      const { applied, deferred, failed } = mergeLimitsPatchIntoModel(
        portfolioInstructionsPatch
      );
      nextPortfolioInstructions = applied;
      pendingLimitsPatch.value = deferred ? portfolioInstructionsPatch : null;
      if (failed) {
        emit(
          "error:ai-merge",
          new Error(
            "AI limits pattern could not be expanded to the contract range"
          )
        );
      }
    }

    // Single combined update so a follow-up emit can't overwrite the
    // freshly-merged limits with a stale portfolio value.
    portfolio.value = {
      ...portfolio.value,
      action_instructions: actionInstructions.value,
      portfolio_instructions:
        nextPortfolioInstructions ?? portfolio.value.portfolio_instructions,
    };
  } catch (error) {
    emit("error:ai-merge", error instanceof Error ? error : String(error));
  }
};

// Retry a deferred AI limits patch once both contract dates are populated.
watch(
  () => [
    actionInstructions.value.start_date,
    actionInstructions.value.end_date,
  ],
  ([start, end]) => {
    if (!pendingLimitsPatch.value || !start || !end) return;
    const { applied, deferred, failed } = mergeLimitsPatchIntoModel(
      pendingLimitsPatch.value
    );
    if (applied) {
      pendingLimitsPatch.value = null;
      updatePortfolioInstructions(applied);
    } else if (!deferred) {
      pendingLimitsPatch.value = null;
      if (failed) {
        emit(
          "error:ai-merge",
          new Error(
            "AI limits pattern could not be expanded to the contract range"
          )
        );
      }
    }
  }
);

// Handle field touched events from child components (allocation section only)
const handleFieldTouched = (field: string) => {
  if (field === "allocate_percentage") {
    v$.value.allocate_percentage.$touch();
  } else if (field === "limits") {
    v$.value.limits.$touch();
  }
};

// Sync when parent provides/updates action_instructions (e.g., after AI analysis)
watch(
  () => portfolio.value.action_instructions,
  (newVal) => {
    if (newVal) {
      actionInstructions.value = { ...(newVal as ActionInstructions) };
    }
  },
  { deep: true }
);

// Per-step validation: touch only the fields relevant to the current step.
const recipientOptionsReady = computed(
  () =>
    !props.usesCnmcIssuance ||
    actionInstructions.value.transaction_type !== "SEND" ||
    destinationsReady.value
);
const validateStep = (stepIndex: number): boolean => {
  if (!recipientOptionsReady.value) return false;
  if (stepIndex === 0) {
    v$.value.name.$touch();
    const counterpartyOk =
      counterpartyDetailsSectionRef.value?.touchAndValidate?.() ?? true;
    const datesOk = contractDatesSectionRef.value?.touchAndValidate?.() ?? true;
    return !v$.value.name.$invalid && counterpartyOk && datesOk;
  }
  if (stepIndex === 1) {
    v$.value.allocate_percentage.$touch();
    v$.value.limits.$touch();
    const childOk =
      deviceSelectionSectionRef.value?.touchAndValidate?.() ?? true;
    return (
      !v$.value.allocate_percentage.$invalid &&
      !v$.value.limits.$invalid &&
      childOk
    );
  }
  if (stepIndex === 2) {
    return deliveryTimingSectionRef.value?.touchAndValidate?.() ?? true;
  }
  return true;
};

const goNext = () => {
  if (!validateStep(currentStep.value)) return;
  if (currentStep.value < wizardSteps.value.length - 1) {
    currentStep.value += 1;
  }
};

const goBack = () => {
  if (currentStep.value > 0) currentStep.value -= 1;
};

// Validation status for parent component
const isValid = computed(() => {
  if (!recipientOptionsReady.value) return false;
  // Check allocation section validation
  if (v$.value.$invalid) return false;

  // Check child component validations
  if (
    counterpartyDetailsSectionRef.value &&
    !counterpartyDetailsSectionRef.value.isValid()
  ) {
    return false;
  }

  if (
    deviceSelectionSectionRef.value &&
    !deviceSelectionSectionRef.value.isValid()
  ) {
    return false;
  }

  if (
    deliveryTimingSectionRef.value &&
    !deliveryTimingSectionRef.value.isValid()
  ) {
    return false;
  }

  if (
    contractDatesSectionRef.value &&
    !contractDatesSectionRef.value.isValid()
  ) {
    return false;
  }

  return true;
});

const touchAndValidateAll = (): boolean => {
  v$.value.$touch();

  if (counterpartyDetailsSectionRef.value) {
    counterpartyDetailsSectionRef.value.touchAndValidate();
  }
  if (contractDatesSectionRef.value) {
    contractDatesSectionRef.value.touchAndValidate();
  }
  if (deviceSelectionSectionRef.value) {
    deviceSelectionSectionRef.value.touchAndValidate();
  }
  if (deliveryTimingSectionRef.value) {
    deliveryTimingSectionRef.value.touchAndValidate();
  }

  return isValid.value;
};

const handleSave = () => {
  if (!touchAndValidateAll()) {
    // Jump back to the first invalid step so the user sees errors in context.
    if (props.mode === "wizard") {
      const firstInvalid = [0, 1, 2].find((step) => !validateStep(step));
      if (firstInvalid !== undefined && firstInvalid !== currentStep.value) {
        currentStep.value = firstInvalid;
      }
    }
    return;
  }
  emit("save");
};

const handleCancel = () => emit("cancel");

const showAll = () => emit("show-all");

// Expose methods for parent component
defineExpose({
  isValid: () => isValid.value,
  touchAndValidate: () => touchAndValidateAll(),
  currentStep,
  goBack,
});
</script>

<style scoped lang="scss">
.ppa-instructions-editor {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  min-height: 100%;
}

// When mounted inside a Phoenix .modal-body (which applies horizontal
// padding), bleed the editor out to the modal edges so the step rail,
// section dividers, and sticky footer span the full modal width like the
// design intends.
.ppa-instructions-editor.is-wizard {
  margin: 0 calc(-1 * var(--phoenix-modal-padding, 1rem));
}

.ppa-instructions-editor__body {
  flex: 1 1 auto;
}

// The prefill-from-document assistant is hoisted out of the paged body so it
// persists across wizard steps; a divider sets it apart from the step content.
.ppa-instructions-editor__assistant {
  border-bottom: 1px solid var(--phoenix-border-color, var(--phoenix-gray-200));
}

.ppa-instructions-editor__review {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ppa-instructions-editor__show-all {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--phoenix-primary);
  background: transparent;
  border: 0;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.12s ease;

  &:hover {
    background: var(--phoenix-primary-bg-subtle);
    color: var(--phoenix-primary);
  }
}

.ppa-instructions-editor__step-indicator {
  font-size: 12px;
  color: var(--phoenix-gray-500);
  margin-right: 8px;
  font-variant-numeric: tabular-nums;
}

.ppa-instructions-editor__field-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--phoenix-gray-700);
  letter-spacing: 0.01em;
  margin-bottom: 6px;
  padding: 0;
  text-transform: none;
  line-height: 1.2;
}

// Match the wizard's typographic scale for all form labels rendered inside
// the editor — otherwise the Phoenix .form-label default (uppercase, tiny,
// extra-bold, with padding-left) feels heavier than the surrounding
// section headings and wizard prompts.
.ppa-instructions-editor :deep(.form-label) {
  padding-left: 0;
  text-transform: none;
  font-size: 12px;
  font-weight: 600;
  color: var(--phoenix-gray-700);
  letter-spacing: 0.01em;
  line-height: 1.2;
  margin-bottom: 6px;
}

.ppa-instructions-editor :deep(.form-label svg) {
  color: var(--phoenix-gray-400);
}
</style>
